import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { PlusCircle, MessageCircle, Edit, Trash2, Calendar, User, ArrowUp, ArrowDown, Heart, Handshake, Share2 } from "lucide-react";
import { useState } from "react";
import { CreatePostDialog } from "@/components/CreatePostDialog";
import { GroupPostComments } from "./GroupPostComments";
import { GroupPostCategories, GroupPostCategory } from "./GroupPostCategories";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CreateGroupPost } from "./CreateGroupPost";

type SortOption = 'newest' | 'oldest' | 'most_discussed';

interface GroupPostsProps {
  groupId: string;
  isAdmin: boolean;
}

export const GroupPosts = ({ groupId, isAdmin }: GroupPostsProps) => {
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedPost, setSelectedPost] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: posts, isLoading, refetch } = useQuery({
    queryKey: ['group-posts', groupId, sortBy],
    queryFn: async () => {
      let query = supabase
        .from('group_posts')
        .select(`
          *,
          posts:posts!inner (
            id,
            user_id,
            content,
            image_url,
            video_url,
            category,
            is_anonymous,
            is_private,
            custom_tags,
            likes_count,
            created_at,
            profiles:profiles!inner (
              username,
              avatar_url
            )
          )
        `)
        .eq('group_id', groupId);

      // Don't filter by status for admins so they can see pending posts too
      if (!isAdmin) {
        query = query.eq('status', 'approved');
      }

      switch (sortBy) {
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'most_discussed':
          query = query.order('comments_count', { ascending: false });
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const { data: comments, refetch: refetchComments } = useQuery({
    queryKey: ['group-post-comments', selectedPost],
    queryFn: async () => {
      if (!selectedPost) return [];
      
      const { data, error } = await supabase
        .from('group_post_comments')
        .select(`
          *,
          profiles:profiles!inner (
            username,
            avatar_url
          )
        `)
        .eq('post_id', selectedPost)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedPost,
  });

  const handleLike = async (postId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to like posts",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('post_reactions')
        .insert([{
          post_id: postId,
          user_id: user.id,
          reaction_type: 'like'
        }]);

      if (error) throw error;

      toast({
        description: "Post liked! 💖",
      });
      refetch();
    } catch (error) {
      console.error('Error liking post:', error);
      toast({
        title: "Error",
        description: "Could not like the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSupport = async (postId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to support posts",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('post_reactions')
        .insert([{
          post_id: postId,
          user_id: user.id,
          reaction_type: 'support'
        }]);

      if (error) throw error;

      toast({
        description: "Support given! 💜",
      });
      refetch();
    } catch (error) {
      console.error('Error supporting post:', error);
      toast({
        title: "Error",
        description: "Could not support the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleShare = (postId: string) => {
    if (navigator.share) {
      navigator.share({
        title: 'Share post',
        url: `${window.location.origin}/post/${postId}`,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/post/${postId}`);
      toast({
        description: "Link copied to clipboard!",
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    try {
      const { error } = await supabase
        .from('group_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      toast({
        title: "Post deleted",
        description: "The post has been deleted successfully.",
      });
      refetch();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Could not delete the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Create post form */}
      <div className="mb-8">
        <CreateGroupPost 
          groupId={groupId}
          onPostCreated={() => refetch()}
        />
      </div>

      {/* Create Post Dialog using CreatePostDialog */}
      <CreatePostDialog 
        open={isCreatePostOpen}
        onOpenChange={setIsCreatePostOpen}
        groupId={groupId}
        onPostCreated={() => refetch()}
      />
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-3xl shadow-md border border-pink-100 dark:border-pink-800/30">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-pink-600 to-purple-600 text-transparent bg-clip-text">Group Posts</h3>
        <div className="flex flex-wrap items-center gap-3 ml-auto">
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
            <SelectTrigger className="w-[180px] border-0 bg-gradient-to-r from-pink-50 to-purple-50 dark:bg-gray-700 rounded-full">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="rounded-lg border-0 shadow-lg">
              <SelectItem value="newest" className="focus:bg-pink-50">
                <div className="flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-pink-500" />
                  Newest first
                </div>
              </SelectItem>
              <SelectItem value="oldest" className="focus:bg-pink-50">
                <div className="flex items-center gap-2">
                  <ArrowDown className="w-4 h-4 text-pink-500" />
                  Oldest first
                </div>
              </SelectItem>
              <SelectItem value="most_discussed" className="focus:bg-pink-50">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-pink-500" />
                  Most discussed
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[200px] w-full rounded-3xl" />
          ))}
        </div>
      ) : !posts?.length ? (
        <div className="text-center py-12 bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 rounded-3xl border border-pink-200 dark:border-pink-800/30">
          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={() => setIsCreatePostOpen(true)}
              className="w-12 h-12 p-0 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white shadow-lg"
            >
              <PlusCircle className="w-6 h-6" />
            </Button>
            <h3 className="text-xl font-medium text-gray-600 dark:text-gray-300">No posts in this group yet</h3>
            <p className="text-gray-500 dark:text-gray-400">Be the first to share something with the group!</p>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {posts.map((groupPost) => (
            <Dialog key={groupPost.id}>
              <div className={`border-0 rounded-3xl p-5 space-y-4 transition-all hover:shadow-lg ${
                groupPost.category === 'discussion' 
                  ? 'bg-gradient-to-br from-pink-100 to-red-50 dark:from-pink-900/30 dark:to-red-900/30' 
                  : groupPost.category === 'announcement'
                  ? 'bg-gradient-to-br from-orange-100 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30'
                  : 'bg-gradient-to-br from-blue-100 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 overflow-hidden rounded-full bg-gradient-to-br from-pink-200 to-purple-200 flex items-center justify-center shadow-md">
                      {(groupPost.posts.profiles as any)?.avatar_url ? (
                        <img 
                          src={(groupPost.posts.profiles as any).avatar_url} 
                          alt={(groupPost.posts.profiles as any).username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-pink-500" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium">
                        {(groupPost.posts.profiles as any)?.username}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDistanceToNow(new Date(groupPost.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700 rounded-full">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeletePost(groupPost.id)}
                        className="text-gray-500 hover:text-red-500 rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <GroupPostCategories
                  category={groupPost.category as GroupPostCategory}
                  tags={groupPost.tags || []}
                />

                <p className="text-base">{groupPost.posts.content}</p>

                {groupPost.posts.image_url && (
                  <img
                    src={groupPost.posts.image_url}
                    alt="Post attachment"
                    className="rounded-2xl max-h-96 object-cover w-full shadow-sm"
                  />
                )}

                {groupPost.posts.video_url && (
                  <video 
                    src={groupPost.posts.video_url} 
                    controls 
                    className="rounded-2xl max-h-96 w-full shadow-sm"
                  >
                    <track kind="captions" />
                  </video>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleLike(groupPost.posts.id)}
                      className="flex items-center gap-2 rounded-full hover:bg-pink-100 dark:hover:bg-pink-900/30 text-gray-600 dark:text-gray-300 hover:text-pink-600"
                    >
                      <Heart className="w-4 h-4" />
                      <span>{groupPost.posts.likes_count || 0}</span>
                    </Button>
                    <DialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="flex items-center gap-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-600 dark:text-gray-300 hover:text-blue-600"
                        onClick={() => setSelectedPost(groupPost.id)}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>{groupPost.comments_count || 0}</span>
                      </Button>
                    </DialogTrigger>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleSupport(groupPost.posts.id)}
                      className="flex items-center gap-2 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/30 text-gray-600 dark:text-gray-300 hover:text-purple-600"
                    >
                      <Handshake className="w-4 h-4" />
                      <span>Support</span>
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleShare(groupPost.posts.id)}
                      className="flex items-center gap-2 rounded-full hover:bg-green-100 dark:hover:bg-green-900/30 text-gray-600 dark:text-gray-300 hover:text-green-600"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <DialogContent className="max-w-2xl max-h-[90vh] p-0 rounded-3xl border-0 overflow-hidden bg-white/90 backdrop-blur-lg dark:bg-gray-800/90">
                <DialogHeader className="p-4 border-b">
                  <DialogTitle>Comments</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[calc(90vh-8rem)] p-4">
                  <GroupPostComments
                    postId={groupPost.id}
                    comments={comments || []}
                    onCommentAdded={() => {
                      refetchComments();
                      refetch();
                    }}
                  />
                </ScrollArea>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      )}
    </div>
  );
};
